import { ref, computed, watch, onBeforeUnmount, ComputedRef } from 'vue';
import { useRouter } from 'vue-router/composables';
import { PleasedClient } from '@/pleased-sdk/PleasedClient';
import { logEvent } from '@/common/api/logger';
import { CallStatus } from './callStatus';

export { CallStatus };

/**
 * Twilio agent voice call lifecycle (PleasedClient + @twilio/voice-sdk).
 * Do not use for landing Voice AI — see useLandingAiVoiceCallManager.
 */
export const useTwilioVoiceCallManager = ({
  voiceChatId,
  onConnected,
  onDisconnect,
}: {
  voiceChatId: ComputedRef<number>;
  onConnected?: () => void;
  onDisconnect?: (data: { voiceChatId: number; status: CallStatus }) => void;
}) => {
  const router = useRouter();

  const voiceCallTime = ref(0);
  const currentStatus = ref<CallStatus>(CallStatus.CONNECTING);

  const voiceCallDuration = computed(() => {
    const seconds = voiceCallTime.value;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  });

  const voice = computed(() => {
    return voiceChatId.value ? PleasedClient.get('voice', voiceChatId.value) : null;
  });

  let voiceCallTimerId = 0;

  const handleConnected = () => {
    logEvent({
      step: 'useTwilioVoiceCallManager handleConnected',
      voice: voice.value,
      currentStatus: currentStatus.value,
    });
    currentStatus.value = CallStatus.CONNECTED;
    voiceCallTimerId = window.setInterval(() => {
      voiceCallTime.value += 1;
    }, 1000);
    onConnected?.();
  };

  const handleDisconnect = () => {
    logEvent({
      step: 'useTwilioVoiceCallManager handleDisconnect',
      voice: voice.value,
      currentStatus: currentStatus.value,
    });

    onDisconnect?.({ voiceChatId: voiceChatId.value, status: currentStatus.value });
    stopVoiceCallTimer();
  };

  const hangUp = () => {
    voice.value?.hangUp();
    stopVoiceCallTimer();
  };

  const startVoiceCallTimer = () => {
    if (!voiceChatId.value) return;

    const currentVoice = PleasedClient.get('voice', voiceChatId.value);

    if (!currentVoice) {
      logEvent({ step: 'useTwilioVoiceCallManager no voice', voice: currentVoice });
      router.back();
      return;
    }

    currentStatus.value = currentVoice.status === 'connected' ? CallStatus.CONNECTED : CallStatus.CONNECTING;
    voiceCallTime.value = currentVoice.connectedTimestamp
      ? Math.floor((Date.now() - currentVoice.connectedTimestamp) / 1000)
      : 0;

    currentVoice.on('voice:connect', handleConnected).on('voice:disconnect', handleDisconnect);
    currentVoice.start();

    if (currentStatus.value === CallStatus.CONNECTED) {
      handleConnected();
    }
  };

  const stopVoiceCallTimer = () => {
    if (voiceCallTimerId) {
      clearInterval(voiceCallTimerId);
      voiceCallTimerId = 0;
    }
    voiceCallTime.value = 0;
    currentStatus.value = CallStatus.CONNECTING;

    if (voice.value) {
      voice.value.off('voice:connect', handleConnected).off('voice:disconnect', handleDisconnect);
    }
  };

  watch(
    voiceChatId,
    (newId, oldId) => {
      if (newId) {
        startVoiceCallTimer();
      } else if (oldId) {
        stopVoiceCallTimer();
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    logEvent({
      step: 'useTwilioVoiceCallManager onBeforeUnmount',
      voice: voice.value,
      currentStatus: currentStatus.value,
    });
    stopVoiceCallTimer();
    voice.value?.hangUp();
  });

  return {
    voiceCallDuration,
    currentStatus,
    reset: stopVoiceCallTimer,
    hangUp,
  };
};
